import { useEffect, useRef, useState } from "react";
import { Play, Facebook, Instagram, Youtube, X, ExternalLink, Loader2 } from "lucide-react";
import { parseVideo } from "@/config/journeys";
import { trJourney } from "@/config/journeyI18n";
import { useLanguage } from "@/context/LanguageContext";

const PlatformIcon = ({ platform }) => {
  if (platform === "facebook") return <Facebook className="h-4 w-4" />;
  if (platform === "instagram") return <Instagram className="h-4 w-4" />;
  return <Youtube className="h-4 w-4" />;
};

// Build an inline-embeddable player URL per platform. Returns null when the
// platform can't be embedded (we then fall back to opening the link).
function embedUrl(v) {
  if (v.platform === "youtube")
    return `https://www.youtube.com/embed/${v.id}?autoplay=1&playsinline=1&rel=0`;
  if (v.platform === "facebook")
    return `https://www.facebook.com/plugins/video.php?href=${encodeURIComponent(
      v.url
    )}&show_text=false&autoplay=true`;
  if (v.platform === "instagram") {
    const m = v.url.match(/instagram\.com\/(reel|p|tv)\/([\w-]+)/i);
    return m
      ? `https://www.instagram.com/${m[1]}/${m[2]}/embed`
      : `${v.url.replace(/\/?$/, "/")}embed`;
  }
  return null;
}

// "Vani" — a responsive video carousel of YouTube/Instagram/Facebook shorts.
// Shows ~2 on mobile and ~4-5 on desktop. Tapping a card plays the reel INLINE
// in a lightbox (no new tab).
export const VideoCarousel = ({ module }) => {
  const videos = (module.videos || []).map(parseVideo).filter(Boolean);
  const [active, setActive] = useState(null);
  const [loading, setLoading] = useState(false);
  const pushedRef = useRef(false);

  // Closing routes through the pushed history entry (if any) so the phone's
  // hardware Back button and our own controls behave identically.
  const closeVideo = () => {
    if (pushedRef.current) {
      pushedRef.current = false;
      window.history.back(); // pops our entry -> popstate closes the modal
    } else {
      setActive(null);
    }
  };

  // While a reel is open: (1) show a loader until the iframe loads, (2) push a
  // history entry so the mobile Back button CLOSES the video instead of leaving
  // the site, and (3) allow Escape to close it.
  useEffect(() => {
    if (!active) return;
    setLoading(true);

    window.history.pushState({ tkhVideo: true }, "");
    pushedRef.current = true;
    const onPop = () => {
      pushedRef.current = false; // our entry was just popped by the back action
      setActive(null);
    };
    const onKey = (e) => e.key === "Escape" && closeVideo();
    window.addEventListener("popstate", onPop);
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("popstate", onPop);
      window.removeEventListener("keydown", onKey);
    };
  }, [active]);

  // Pause the site ambience while a reel is playing inline; resume on close.
  useEffect(() => {
    window.dispatchEvent(
      new CustomEvent("tkh-audio-duck", { detail: { on: !!active } })
    );
    return () => {
      if (active)
        window.dispatchEvent(
          new CustomEvent("tkh-audio-duck", { detail: { on: false } })
        );
    };
  }, [active]);

  if (!videos.length) return null;

  const open = (v) => {
    const embed = embedUrl(v);
    if (embed) setActive({ ...v, embed });
    else window.open(v.url, "_blank", "noopener,noreferrer");
  };

  return (
    <div data-testid={`journey-video-${module.id}`} className="mx-auto max-w-6xl px-5">
      <ModuleHeader title={module.title} subtitle={module.subtitle} />
      <div className="mt-8 flex snap-x snap-mandatory gap-4 overflow-x-auto pb-4 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        {videos.map((v, i) => (
          <button
            key={i}
            type="button"
            onClick={() => open(v)}
            data-testid={`video-card-${i}`}
            className="group relative aspect-[9/16] w-[42vw] max-w-[190px] shrink-0 snap-start overflow-hidden rounded-2xl border border-amber-200/15 bg-gradient-to-br from-[#1a2140] to-[#0a0f24] sm:w-[200px]"
          >
            {v.thumb ? (
              <img
                src={v.thumb}
                alt=""
                loading="lazy"
                className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
              />
            ) : (
              <div className="absolute inset-0 grid place-items-center bg-gradient-to-br from-amber-500/20 to-[#0a0f24]">
                <PlatformIcon platform={v.platform} />
              </div>
            )}
            <div className="absolute inset-0 bg-gradient-to-t from-[#070b18]/85 via-transparent to-transparent" />
            <div className="absolute left-2 top-2 grid h-7 w-7 place-items-center rounded-full bg-black/50 text-amber-200 backdrop-blur">
              <PlatformIcon platform={v.platform} />
            </div>
            <div className="absolute inset-0 grid place-items-center">
              <span className="grid h-12 w-12 place-items-center rounded-full bg-amber-400/90 text-[#0a0f24] shadow-lg transition-transform duration-300 group-hover:scale-110">
                <Play className="h-5 w-5 translate-x-0.5 fill-current" />
              </span>
            </div>
          </button>
        ))}
      </div>

      {active && (
        <div
          data-testid="video-modal"
          onClick={closeVideo}
          className="fixed inset-0 z-[100] grid place-items-center bg-black/85 p-4 backdrop-blur-sm"
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="relative w-full max-w-[420px]"
          >
            <div className="mb-3 flex items-center justify-end gap-2">
              <a
                href={active.url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 rounded-full border border-amber-300/30 px-3 py-1.5 text-xs text-amber-200 hover:bg-amber-400/10"
              >
                <ExternalLink className="h-3.5 w-3.5" /> Open
              </a>
              <button
                data-testid="video-modal-close"
                onClick={closeVideo}
                className="grid h-9 w-9 place-items-center rounded-full bg-white/10 text-white hover:bg-white/20"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="relative aspect-[9/16] w-full overflow-hidden rounded-2xl border border-amber-200/15 bg-black">
              {loading && (
                <div
                  data-testid="video-loading"
                  className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-3 bg-black"
                >
                  <Loader2 className="h-8 w-8 animate-spin text-amber-300" />
                  <span className="text-xs tracking-[0.25em] text-amber-100/50">
                    LOADING…
                  </span>
                </div>
              )}
              <iframe
                data-testid="video-modal-iframe"
                src={active.embed}
                title="Vani video"
                onLoad={() => setLoading(false)}
                className="absolute inset-0 h-full w-full"
                allow="autoplay; encrypted-media; picture-in-picture; fullscreen"
                allowFullScreen
                frameBorder="0"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export const ModuleHeader = ({ title, subtitle }) => {
  const { lang } = useLanguage();
  return (
    <div className="text-center">
      <h3 className="font-display text-3xl leading-tight text-amber-200 sm:text-4xl">
        {trJourney(title, lang)}
      </h3>
      {subtitle && (
        <p className="mx-auto mt-3 max-w-2xl text-sm text-amber-50/55 md:text-base">
          {trJourney(subtitle, lang)}
        </p>
      )}
    </div>
  );
};
